import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, newsletterAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [newsletterLimit, setNewsletterLimit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  const checkAuth = async () => {
    // Skip delay if just authenticated
    const justAuth = sessionStorage.getItem('just_authenticated');
    if (!justAuth) {
      await new Promise(r => setTimeout(r, 150));
    } else {
      sessionStorage.removeItem('just_authenticated');
    }

    try {
      const userData = await authAPI.me();
      setUser(userData);
      setIsAuthenticated(true);
      
      // Fetch newsletter limit
      try {
        const limit = await newsletterAPI.getLimit();
        setNewsletterLimit(limit);
      } catch (e) {
        console.error('Failed to fetch newsletter limit:', e);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('session_token');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const loginWithGoogle = () => {
    // Redirect to Emergent Auth
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSessionId = async (sessionId) => {
    try {
      const userData = await authAPI.exchangeSession(sessionId);
      setUser(userData);
      setIsAuthenticated(true);
      sessionStorage.setItem('just_authenticated', 'true');
      
      // Fetch newsletter limit
      try {
        const limit = await newsletterAPI.getLimit();
        setNewsletterLimit(limit);
      } catch (e) {
        console.error('Failed to fetch newsletter limit:', e);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Session exchange failed:', error);
      return { success: false, error: error.message };
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const userData = await authAPI.login(email, password);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Fetch newsletter limit
      try {
        const limit = await newsletterAPI.getLimit();
        setNewsletterLimit(limit);
      } catch (e) {
        console.error('Failed to fetch newsletter limit:', e);
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const registerWithEmail = async (email, password, name) => {
    try {
      const userData = await authAPI.register(email, password, name);
      setUser(userData);
      setIsAuthenticated(true);
      
      // New user starts with empty limit
      setNewsletterLimit({
        used: 0,
        limit: 2,
        reset_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        can_create: true,
      });
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    setNewsletterLimit(null);
    setIsAuthenticated(false);
  };

  const refreshLimit = async () => {
    try {
      const limit = await newsletterAPI.getLimit();
      setNewsletterLimit(limit);
    } catch (e) {
      console.error('Failed to refresh limit:', e);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        newsletterLimit,
        isLoading,
        isAuthenticated,
        loginWithGoogle,
        processSessionId,
        loginWithEmail,
        registerWithEmail,
        logout,
        refreshLimit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
